import React, { useState, useContext } from 'react';
import styled from 'styled-components';
import { ThemeContext } from '../contexts/ThemeContext';
import { useNavigate } from 'react-router-dom';

const ForgetPasswordPage = () => {
  const { darkMode } = useContext(ThemeContext);
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) {
      setError('Email harus diisi');
      return;
    }
    setError('');
    setIsLoading(true);

    try {
      const baseUrl = (process.env.REACT_APP_API_BASE_URL || 'http://127.0.0.1:8000/').replace(/\/$/, '');
      await fetch(`${baseUrl}/api/forgot-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify({ email }),
      });

      // For security, always show the success message regardless of the outcome
      setIsSubmitted(true);

    } catch (err) {
      // Also show success on network error to prevent backend state discovery
      setIsSubmitted(true);
    } finally {
      setIsLoading(false);
    }
  };

  const goToLogin = () => {
    navigate('/');
  };

  if (isSubmitted) {
    return (
      <PageContainer>
        <FormContainer>
          <FormWrapper theme={darkMode ? 'dark' : 'light'}>
            <SuccessMessage>
              <SuccessIcon>✓</SuccessIcon>
              <div>
                <h3>Permintaan Terkirim</h3>
                <p>Jika email yang Anda masukkan terdaftar, kami akan mengirimkan password baru.</p>
              </div>
            </SuccessMessage>
            <FormActions>
              <BackToLoginButton onClick={goToLogin}>
                Kembali ke Halaman Login
              </BackToLoginButton>
            </FormActions>
          </FormWrapper>
        </FormContainer>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <FormContainer>
        <FormWrapper theme={darkMode ? 'dark' : 'light'}>
          <FormHeader>
            <h2>Reset Password</h2>
            <p>Masukkan email Anda untuk mereset password</p>
          </FormHeader>
          
          <FormContent>
            <form onSubmit={handleSubmit}>
              <FormGroup theme={darkMode ? 'dark' : 'light'}>
                <label htmlFor="email">Email</label>
                <input
                  type="email"
                  id="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Masukkan email Anda"
                  required
                />
                {error && <ErrorMessage>{error}</ErrorMessage>}
              </FormGroup>
              
              <FormActions>
                <BackButton type="button" onClick={goToLogin} disabled={isLoading}>
                  Kembali
                </BackButton>
                <SubmitButton type="submit" disabled={isLoading}>
                  {isLoading ? 'Mengirim...' : 'Kirim Link Reset'}
                </SubmitButton>
              </FormActions>
              
              {isLoading && (
                <LoadingMessage>
                  Mohon tunggu. Sedang proses.
                </LoadingMessage>
              )}
            </form>
          </FormContent>
        </FormWrapper>
      </FormContainer>
    </PageContainer>
  );
};

const PageContainer = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1.5rem;
  background-color: var(--background);
`;

const FormContainer = styled.div`
  width: 100%;
  max-width: 400px;
  margin: 0 auto;
`;

const FormWrapper = styled.div`
  background-color: ${props => props.theme === 'dark' ? '#2d2d2d' : 'white'};
  border-radius: 16px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  overflow: hidden;
`;

const FormHeader = styled.div`
  padding: 1.5rem;
  text-align: center;
  border-bottom: 1px solid var(--border);
  
  h2 {
    font-size: 1.5rem;
    font-weight: 600;
    color: var(--text-dark);
    margin-bottom: 0.5rem;
  }
  
  p {
    color: var(--text-light);
    font-size: 0.875rem;
  }
`;

const FormContent = styled.div`
  padding: 1.5rem;
`;

const FormGroup = styled.div`
  margin-bottom: 1.5rem;
  
  label {
    display: block;
    margin-bottom: 0.5rem;
    font-weight: 500;
    color: var(--text-dark);
  }
  
  input {
    width: 100%;
    padding: 0.75rem;
    border: 1px solid var(--border);
    border-radius: 8px;
    font-family: inherit;
    font-size: 1rem;
    color: var(--text-dark);
    background-color: ${props => props.theme === 'dark' ? '#383838' : '#f5f5f5'};
    
    &:focus {
      border-color: var(--primary);
      box-shadow: 0 0 0 2px rgba(43, 93, 161, 0.2);
      outline: none;
    }
  }
`;

const FormActions = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 2rem;
`;

const Button = styled.button`
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  flex: 1;
`;

const SubmitButton = styled(Button)`
  background-color: var(--primary);
  color: white;
  border: none;
  
  &:hover {
    background-color: var(--primary-light);
  }

  &:disabled {
    background-color: var(--primary-light);
    cursor: not-allowed;
  }
`;

const BackButton = styled(Button)`
  background-color: transparent;
  color: var(--text-dark);
  border: 1px solid var(--border);
  
  &:hover {
    background-color: var(--background-light);
  }
`;

const BackToLoginButton = styled(Button)`
  background-color: var(--primary);
  color: white;
  border: none;
  width: 100%;
  text-align: center;
  
  &:hover {
    background-color: var(--primary-light);
  }
`;

const SuccessMessage = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 2rem 1.5rem;
  text-align: center;
  flex-direction: column;
  
  h3 {
    font-size: 1.25rem;
    font-weight: 600;
    color: var(--text-dark);
    margin-bottom: 0.5rem;
  }
  
  p {
    color: var(--text-light);
  }
`;

const SuccessIcon = styled.div`
  width: 48px;
  height: 48px;
  background-color: #e6f8f5;
  color: #00a389;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  font-weight: bold;
`;

const ErrorMessage = styled.div`
  color: #dc3545;
  font-size: 0.875rem;
  margin-top: 0.5rem;
`;

const LoadingMessage = styled.div`
  color: var(--text-light);
  font-size: 0.875rem;
  margin-top: 0.5rem;
  text-align: center;
`;

export default ForgetPasswordPage; 